import { useCallback, useEffect, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from 'react'
import { Room, RoomEvent, Track } from 'livekit-client'
import {
  buildConnectBody,
  deleteRoom,
  deleteSession,
  fetchLiveKitConnect,
  fetchTrainerConnect,
  micAvailabilityHint,
  withTimeout,
} from '../lib/livekitConnect'
import VoiceMeter from '../components/VoiceMeter'
import { useProgramName } from '../components/ModelGate'
import { usePanel } from '../lib/panelContext'
import { useTalkLog, type LogTag } from '../lib/talkLog'

type AgentPresence = 'offline' | 'waiting' | 'in_room' | 'speaking'

type LiveCaption = {
  role: 'user' | 'agent' | null
  text: string
  partial: boolean
}

const CONNECT_TIMEOUT_MS = 25_000
const ROOM_CONNECT_TIMEOUT_MS = 18_000
const CHAT_TOPIC = 'lk.chat'
const TRANSCRIPTION_TOPIC = 'lk.transcription'

type ChatRole = 'user' | 'agent'

type ChatMessage = {
  id: string
  role: ChatRole
  text: string
  partial: boolean
}

/** Join text-stream chunks. Deltas append; a cumulative snapshot replaces. */
export function mergeStreamText(previous: string, chunk: string): string {
  if (!chunk) return previous
  if (chunk.startsWith(previous)) return chunk
  return previous + chunk
}

type TranscriptReader = {
  info: { id: string; attributes?: Record<string, string> }
  [Symbol.asyncIterator](): AsyncIterator<string>
}

function looksLikeAgentIdentity(identity: string): boolean {
  return /agent/i.test(identity || '')
}

export type TalkMode = 'text' | 'voice' | 'trainer'

type TalkProps = {
  mode?: TalkMode
  /** Trainer mode only: sent as X-Trainer-Token to open a reflection session. */
  trainerToken?: string
  /** Rendered at the start of the connection toolbar, so mode controls share that row. */
  toolbarLeading?: ReactNode
  /** Shown under the toolbar, used for the training note. */
  toolbarNote?: ReactNode
}

export default function Talk({ mode = 'voice', trainerToken = '', toolbarLeading, toolbarNote }: TalkProps) {
  const programName = useProgramName()
  const isVoice = mode === 'voice'
  const showText = mode !== 'voice'
  const manualStart = mode === 'trainer'
  const startBlocked = manualStart && !trainerToken.trim()
  const {
    backendUrl,
    connected,
    setConnected,
    connecting,
    setConnecting,
    setActiveRoom,
    refreshPanel,
    registerLeaveSession,
  } = usePanel()

  const [status, setStatus] = useState('Idle')
  const [statusCls, setStatusCls] = useState('')
  const [micEnabled, setMicEnabled] = useState(false)
  const [agentPresence, setAgentPresence] = useState<AgentPresence>('offline')
  const [liveCaption, setLiveCaption] = useState<LiveCaption>({
    role: null,
    text: '',
    partial: false,
  })
  const { log, updateLine } = useTalkLog()
  const roomRef = useRef<Room | null>(null)
  const sessionRef = useRef<{ roomName: string; identity: string } | null>(null)
  const endingRef = useRef(false)
  const partialsRef = useRef<Map<string, number>>(new Map())
  const chatRef = useRef<HTMLDivElement>(null)
  const draftRef = useRef<HTMLTextAreaElement>(null)
  const audioEls = useRef<HTMLAudioElement[]>([])
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [draft, setDraft] = useState('')
  const [pendingReply, setPendingReply] = useState(false)
  const replyBaseline = useRef<Set<string> | null>(null)

  const canStartConversation = !connected && !connecting

  const syncAgentPresence = useCallback((room: Room | null) => {
    if (!room || room.state !== 'connected') {
      setAgentPresence('offline')
      return
    }
    const remotes = [...room.remoteParticipants.values()]
    const agents = remotes.filter((p) => looksLikeAgentIdentity(p.identity))
    if (agents.length === 0) {
      setAgentPresence(connected || connecting ? 'waiting' : 'offline')
      return
    }
    const speaking = agents.some((p) => p.isSpeaking)
    setAgentPresence(speaking ? 'speaking' : 'in_room')
  }, [connected, connecting])

  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight
    }
  }, [messages, pendingReply])

  const upsertChat = useCallback((id: string, role: ChatRole, text: string, partial: boolean) => {
    setMessages((prev) => {
      const index = prev.findIndex((message) => message.id === id)
      if (index >= 0) {
        const next = prev.slice()
        next[index] = { ...next[index], text, partial }
        return next
      }
      return [...prev, { id, role, text, partial }]
    })
    if (role === 'agent' && replyBaseline.current && !replyBaseline.current.has(id) && text.trim()) {
      replyBaseline.current = null
      setPendingReply(false)
    }
  }, [])

  useEffect(() => {
    log('sys', `Talk loaded at ${location.href}`)
    log('sys', 'Start chat when you are ready.')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const upsertTranscript = (
    participantIdentity: string,
    seg: { id: string; text?: string; final?: boolean },
    isSelf: boolean,
  ) => {
    const key = `${participantIdentity}|${seg.id}`
    const existing = partialsRef.current.get(key)
    const tag: LogTag = isSelf ? 'user' : 'agent'
    const text = seg.text || ''
    const partial = seg.final === false
    setLiveCaption({
      role: isSelf ? 'user' : 'agent',
      text: text || (partial ? '…' : ''),
      partial,
    })
    if (existing != null) {
      updateLine(existing, text, partial)
    } else {
      const id = log(tag, text, { partial })
      partialsRef.current.set(key, id)
    }
    if (seg.final) {
      partialsRef.current.delete(key)
      // Keep final caption briefly visible, then clear when next speech starts.
      if (text) {
        window.setTimeout(() => {
          setLiveCaption((prev) =>
            prev.text === text && !prev.partial
              ? { role: null, text: '', partial: false }
              : prev,
          )
        }, 2_500)
      }
    }
  }

  const handleStop = useCallback(async () => {
    if (endingRef.current) return
    const session = sessionRef.current
    const live = roomRef.current
    if (!session && !live) return
    endingRef.current = true
    roomRef.current = null
    sessionRef.current = null
    try {
      if (live) await live.disconnect()
    } catch {
      /* already gone */
    }
    audioEls.current.forEach((el) => el.remove())
    audioEls.current = []
    if (session) {
      await Promise.allSettled([
        deleteRoom(backendUrl, session.roomName),
        deleteSession(backendUrl, session.identity),
      ])
    }
    setConnected(false)
    setMicEnabled(false)
    setStatus('Idle')
    setStatusCls('')
    setActiveRoom('')
    setAgentPresence('offline')
    setLiveCaption({ role: null, text: '', partial: false })
    setPendingReply(false)
    replyBaseline.current = null
    void refreshPanel()
    endingRef.current = false
    log('sys', 'Chat ended.')
  }, [backendUrl, log, refreshPanel, setActiveRoom, setConnected])

  const consumeTranscript = useCallback(
    async (reader: TranscriptReader, identity: string) => {
      const room = roomRef.current
      const isSelf = !!room && identity === room.localParticipant.identity
      const role: ChatRole = isSelf ? 'user' : 'agent'
      const segment = reader.info.attributes?.['lk.segment_id'] || reader.info.id
      const key = `${identity}|${segment}`
      let text = ''
      try {
        for await (const chunk of reader) {
          text = mergeStreamText(text, chunk)
          if (!text.trim()) continue
          upsertChat(key, role, text, true)
          setLiveCaption({ role, text, partial: true })
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err)
        log('err', `Transcript stream failed: ${message}`)
      }
      const finalText = text.trim()
      if (!finalText) return
      upsertChat(key, role, finalText, false)
      setLiveCaption({ role, text: finalText, partial: false })
    },
    [log, upsertChat],
  )

  const stopRef = useRef(handleStop)
  stopRef.current = handleStop

  useEffect(() => {
    registerLeaveSession(handleStop)
    return () => registerLeaveSession(null)
  }, [handleStop, registerLeaveSession])

  useEffect(() => {
    return () => {
      void stopRef.current()
    }
  }, [])

  const handleStart = async () => {
    // Unguessable: on the public site a guessable room name would let a stranger join someone's call.
    const id = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`
    const roomName = `chat-${id}`
    const userIdentity = `web-${id}`
    sessionRef.current = { roomName, identity: userIdentity }
    log('sys', 'Starting chat')
    setMessages([])
    setDraft('')
    setPendingReply(false)
    replyBaseline.current = null
    setConnecting(true)
    setStatus('Connecting...')
    setStatusCls('')
    try {
      const body = buildConnectBody(userIdentity, 'Web Tester', roomName)
      log('sys', 'Connecting')

      const data = await withTimeout(
        mode === 'trainer'
          ? fetchTrainerConnect(backendUrl, trainerToken.trim())
          : fetchLiveKitConnect(backendUrl, body),
        CONNECT_TIMEOUT_MS,
        'Backend connect',
      )
      log('info', `  url: ${data.url}`)
      log('info', `  agent: ${data.agent_name || '(default)'} · dispatched=${data.agent_dispatched}`)
      if (!data.agent_dispatched) {
        log(
          'sys',
          'No agent joined yet — start the LiveKit voice worker (AI-LiveKit-Agent) with LIVEKIT_* set.',
        )
      }
      setActiveRoom(data.room ?? roomName)

      const room = new Room({ adaptiveStream: true, dynacast: true })
      roomRef.current = room
      setAgentPresence('waiting')
      setLiveCaption({ role: null, text: '', partial: false })

      room.on(RoomEvent.Connected, () => {
        log('ok', `Connected as ${room.localParticipant.identity}`)
        setStatus('Connected')
        setStatusCls('on')
        setConnected(true)
        syncAgentPresence(room)
      })
      room.on(RoomEvent.Disconnected, () => {
        void stopRef.current()
      })
      room.on(RoomEvent.ParticipantConnected, (p) => {
        log('info', `+ ${p.identity}`)
        syncAgentPresence(room)
      })
      room.on(RoomEvent.ParticipantDisconnected, (p) => {
        log('info', `- ${p.identity}`)
        syncAgentPresence(room)
      })
      room.on(RoomEvent.ActiveSpeakersChanged, () => {
        syncAgentPresence(room)
        const localSpeaking = room.localParticipant.isSpeaking
        if (localSpeaking) {
          setLiveCaption((prev) =>
            prev.role === 'user' && prev.text
              ? prev
              : { role: 'user', text: prev.role === 'user' ? prev.text : 'Listening…', partial: true },
          )
        }
      })
      room.on(RoomEvent.TrackSubscribed, (track, _publication, participant) => {
        log('ok', `audio/track from ${participant.identity}`)
        syncAgentPresence(room)
        if (isVoice && track.kind === Track.Kind.Audio) {
          const el = track.attach()
          el.autoplay = true
          document.body.appendChild(el)
          audioEls.current.push(el)
        }
      })
      room.on(RoomEvent.TrackUnsubscribed, (_track, _pub, participant) => {
        if (looksLikeAgentIdentity(participant.identity)) {
          syncAgentPresence(room)
        }
      })
      room.registerTextStreamHandler(TRANSCRIPTION_TOPIC, (reader, participant) => {
        void consumeTranscript(reader, participant.identity)
      })
      room.on(RoomEvent.TranscriptionReceived, (segments, participant) => {
        const identity = participant?.identity ?? room.localParticipant.identity
        const isSelf =
          !participant ||
          participant.identity === room.localParticipant.identity
        for (const seg of segments) {
          upsertTranscript(identity, seg, isSelf)
        }
      })
      room.on(RoomEvent.MediaDevicesError, (e) =>
        log('err', `media devices error: ${e?.message ?? e}`),
      )

      await withTimeout(
        room.connect(data.url, data.token),
        ROOM_CONNECT_TIMEOUT_MS,
        'LiveKit room.connect',
      )
      setStatus('Connected')
      setStatusCls('on')
      setConnected(true)

      const micHint = micAvailabilityHint(
        typeof window !== 'undefined' ? window.isSecureContext : true,
        typeof navigator !== 'undefined' && !!navigator.mediaDevices,
      )
      try {
        if (isVoice) {
          await room.localParticipant.setMicrophoneEnabled(true)
          setMicEnabled(true)
        }
      } catch (micErr) {
        setMicEnabled(false)
        const micMsg =
          micErr instanceof Error ? micErr.message : String(micErr)
        log('err', `Mic not enabled: ${micMsg}`)
        if (micHint) log('sys', micHint)
        else log('sys', 'Allow the microphone, then click Unmute Mic.')
      }

      log('ok', 'Chat ready.')
      void refreshPanel()

      window.setTimeout(() => {
        if (!roomRef.current) return
        syncAgentPresence(roomRef.current)
        const remotes = [...roomRef.current.remoteParticipants.values()]
        const agent = remotes.find((p) => looksLikeAgentIdentity(p.identity))
        const hasAudio = agent
          ? [...agent.audioTrackPublications.values()].some((pub) => !!pub.track)
          : false
        if (!agent) {
          log(
            'sys',
            'Still waiting for agent — check Agents page / remote worker LiveKit connection.',
          )
        } else if (!hasAudio) {
          log('sys', 'Agent is in the room but not publishing audio yet.')
        } else {
          log('ok', 'Agent online in room with audio.')
        }
      }, 4_000)
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      log('err', `FAILED: ${message}`)
      setStatus('Error')
      setStatusCls('err')
      setConnected(false)
      void stopRef.current()
    } finally {
      setConnecting(false)
    }
  }

  // Text chat connects when the page opens. Train mode waits for Start chat, so a partial token never dials.
  useEffect(() => {
    if (mode !== 'text') return
    const timer = window.setTimeout(() => void handleStart(), 0)
    return () => window.clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode])

  const sendDraft = async () => {
    const text = draft.trim()
    const room = roomRef.current
    if (!text || !room || !connected) return
    const id = `local-${Date.now()}`
    setDraft('')
    setMessages((prev) => {
      replyBaseline.current = new Set(prev.map((message) => message.id))
      return [...prev, { id, role: 'user', text, partial: false }]
    })
    setPendingReply(true)
    setLiveCaption({ role: 'user', text, partial: false })
    log('user', text)
    try {
      await room.localParticipant.sendText(text, { topic: CHAT_TOPIC })
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      log('err', `Text send failed: ${message}`)
      setPendingReply(false)
      replyBaseline.current = null
    }
  }

  const handleSendText = (event: FormEvent) => {
    event.preventDefault()
    void sendDraft()
  }

  const handleDraftKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      void sendDraft()
    }
  }

  const handleMuteLocal = async () => {
    const room = roomRef.current
    if (!room) return
    const next = !micEnabled
    try {
      await room.localParticipant.setMicrophoneEnabled(next)
      setMicEnabled(next)
      log('sys', next ? 'Local mic unmuted.' : 'Local mic muted.')
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      log('err', `Mic toggle failed: ${message}`)
      const hint = micAvailabilityHint(
        window.isSecureContext,
        !!navigator.mediaDevices,
      )
      if (hint) log('sys', hint)
    }
  }

  return (
    <>
      <div className="talk-toolbar">
        {toolbarLeading}
        {(isVoice || manualStart || canStartConversation) && (
          <button
            type="button"
            className="primary"
            onClick={() => void handleStart()}
            disabled={!canStartConversation || startBlocked}
            title={startBlocked ? 'Enter the trainer token first' : undefined}
          >
            {connecting ? 'Starting…' : isVoice || manualStart ? 'Start chat' : 'Reconnect'}
          </button>
        )}
        {isVoice && (
          <button type="button" onClick={() => void handleMuteLocal()} disabled={!connected}>
            {!connected || micEnabled ? 'Mute Mic' : 'Unmute Mic'}
          </button>
        )}
        {isVoice && (
          <button
            type="button"
            className="danger"
            onClick={() => void handleStop()}
            disabled={!connected && !connecting}
          >
            End chat
          </button>
        )}
        <div className="spacer" />
        {connected && (
          <span
            className={`agent-presence-pill ${agentPresence}`}
            data-testid="agent-presence"
            title="LiveKit agent presence in this room"
          >
            {agentPresence === 'speaking'
              ? 'Agent speaking'
              : agentPresence === 'in_room'
                ? 'Agent online'
                : agentPresence === 'waiting'
                  ? 'Waiting for agent…'
                  : 'Agent offline'}
          </span>
        )}
        <span className={`status-pill ${statusCls}`}>{status}</span>
      </div>
      {toolbarNote}

      {isVoice && connected && (
        <div className="voice-meters">
          <VoiceMeter room={roomRef.current} label="You" source="local" active={micEnabled} />
          <VoiceMeter room={roomRef.current} label="Agent" source="agent" active={agentPresence !== 'waiting' && agentPresence !== 'offline'} />
        </div>
      )}

      {isVoice && connected && (
        <div
          className={`live-caption ${liveCaption.role ? liveCaption.role : 'idle'}${liveCaption.partial ? ' partial' : ''}`}
          data-testid="live-caption"
          aria-live="polite"
        >
          {liveCaption.role && liveCaption.text ? (
            <>
              <span className="live-caption-role">
                {liveCaption.role === 'user' ? 'You' : 'Agent'}
              </span>
              <span className="live-caption-text">{liveCaption.text}</span>
            </>
          ) : agentPresence === 'waiting' ? (
            <span className="live-caption-text">Waiting for agent to join…</span>
          ) : agentPresence === 'in_room' || agentPresence === 'speaking' ? (
            <span className="live-caption-text">
              Agent online — speak, or type in the text window.
            </span>
          ) : (
            <span className="live-caption-text">Live transcript</span>
          )}
        </div>
      )}

      <div className="talk-body">
        {showText && (
        <section className="text-chat" aria-label="Text chat" data-testid="text-chat">
          <header className="text-chat-head">
            <h2>{mode === 'trainer' ? 'Train' : 'Text'}</h2>
            <p>
              {mode === 'trainer'
                ? 'Corrections in this chat become the handling for that call-flow step.'
                : 'Typed messages go to the agent as text; it answers here.'}
            </p>
          </header>
          <div ref={chatRef} className="text-chat-scroll" aria-live="polite">
            {messages.length === 0 && !pendingReply ? (
              <p className="text-chat-empty">
                {connected
                  ? 'Type a question. The agent answers in this window.'
                  : 'Connecting…'}
              </p>
            ) : (
              messages.map((message) => (
                <article
                  key={message.id}
                  className={`text-chat-bubble ${message.role}${message.partial ? ' partial' : ''}`}
                >
                  <span className="text-chat-who">
                    {message.role === 'user' ? 'You' : programName}
                  </span>
                  <p>{message.text}</p>
                </article>
              ))
            )}
            {pendingReply && (
              <p className="text-chat-pending" data-testid="text-chat-pending">
                Working on a reply
              </p>
            )}
          </div>
          <form className="text-chat-compose" onSubmit={handleSendText}>
            <label className="sr-only" htmlFor="text-chat-draft">
              Message
            </label>
            <textarea
              id="text-chat-draft"
              ref={draftRef}
              rows={2}
              value={draft}
              placeholder={connected ? 'Type what you would say' : 'Connecting…'}
              disabled={!connected}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={handleDraftKeyDown}
            />
            <button type="submit" className="primary" disabled={!connected || !draft.trim()}>
              Send
            </button>
          </form>
        </section>
        )}
      </div>
    </>
  )
}
